import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Save,
  Play,
  ZoomIn,
  ZoomOut,
  Loader2,
  Check,
  Images,
  Code,
  Eye,
  Download,
  Undo2,
  Redo2,
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignStartHorizontal,
  AlignEndHorizontal,
  AlignStartVertical,
  AlignEndVertical,
  Maximize,
  Minimize,
  Grid3x3,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '../../ui/tooltip';
import { CanvasWorkspace } from './CanvasWorkspace';
import { PropertyPanel } from './PropertyPanel';
import { LayersPanel } from './LayersPanel';
import { RenderGallery } from './RenderGallery';
import { RenderProductModal } from './RenderProductModal';
import { useHistory } from '../../canvas-shared/useHistory';
import { flattenRawData, sanitizeImageUrl } from '../../canvas-shared/canvasUtils';
import { ZOOM_PRESETS } from '../../canvas-shared/canvasConstants';
import { CanvasOnboarding } from '../../canvas-shared/CanvasOnboarding';
import { CanvasBreadcrumb } from '../../canvas-shared/CanvasBreadcrumb';
import type { BreadcrumbSegment } from '../../canvas-shared/CanvasBreadcrumb';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../../ui/select';
import { useToast } from '../../../hooks/use-toast';
import { feedWatchService } from '../../../services/feedWatch';
import type {
  CanvasDesign,
  CanvasElement,
  CanvasElementType,
  TextElement,
  ImageElement,
  RectElement,
  CircleElement,
  FeedProduct,
} from '../../../services/feedWatch';

// ── Helpers ─────────────────────────────────────────────────

function productLabel(product: FeedProduct): string {
  const firstKey = Object.keys(product.raw_data)[0];
  const firstVal = firstKey ? String(product.raw_data[firstKey] ?? '') : '';
  const truncated = firstVal.length > 40 ? `${firstVal.slice(0, 40)}...` : firstVal;
  return truncated || `Product #${product.sort_index}`;
}

function resolveProductImageThumb(product: FeedProduct): string | null {
  const raw = product.raw_data;
  // Check common image field names first (including Google/XML feed prefixed variants)
  const imageKeys = [
    'image_link', 'g:image_link', 'image', 'main_image',
    'thumbnail', 'image_url', 'picture', 'photo', 'img',
    'featured_image', 'product_image', 'media_url',
  ];
  for (const key of imageKeys) {
    if (raw[key] && typeof raw[key] === 'string') {
      return sanitizeImageUrl(raw[key] as string);
    }
  }
  // Fallback: find any value that looks like an image URL
  for (const val of Object.values(raw)) {
    if (typeof val === 'string' && /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp|svg)/i.test(val)) {
      return sanitizeImageUrl(val);
    }
  }
  return null;
}

// ── Default element factories ────────────────────────────────

let elementIdCounter = 0;

function createDefaultElement(type: CanvasElementType): CanvasElement {
  const id = `el_${Date.now()}_${++elementIdCounter}`;
  const base = { id, x: 50, y: 50, width: 200, height: 100, visible: true };

  switch (type) {
    case 'text':
      return {
        ...base,
        type: 'text',
        content: 'Text',
        fontSize: 24,
        fontWeight: 'normal',
        fontFamily: 'Inter, sans-serif',
        color: '#000000',
        textAlign: 'left',
      } as TextElement;
    case 'image':
      return {
        ...base,
        type: 'image',
        src: '',
        objectFit: 'cover',
        borderRadius: 0,
        removeBg: false,
      } as ImageElement;
    case 'rect':
      return {
        ...base,
        type: 'rect',
        fill: '#e2e8f0',
        borderRadius: 0,
        border: '',
      } as RectElement;
    case 'circle':
      return {
        ...base,
        width: 100,
        height: 100,
        type: 'circle',
        fill: '#e2e8f0',
      } as CircleElement;
  }
}

// ── Component ────────────────────────────────────────────────

interface CanvasEditorProps {
  feedId: string;
  design: CanvasDesign;
  detectedFields: string[];
  totalProducts?: number;
  onBack: () => void;
  onDesignUpdated: (design: CanvasDesign) => void;
  breadcrumbSegments?: BreadcrumbSegment[];
}

export function CanvasEditor({
  feedId,
  design,
  detectedFields,
  totalProducts,
  onBack,
  onDesignUpdated,
  breadcrumbSegments,
}: CanvasEditorProps) {
  // ── History-backed element state ──
  const history = useHistory<CanvasElement[]>([...design.elements], 50);
  const elements = history.state;

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [designName, setDesignName] = useState(design.name);
  const [zoom, setZoom] = useState(0.75);
  const [showSource, setShowSource] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const [showRenderGallery, setShowRenderGallery] = useState(false);
  const [showRenderModal, setShowRenderModal] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showGrid, setShowGrid] = useState(false);

  // Product data
  const [previewProducts, setPreviewProducts] = useState<FeedProduct[]>([]);
  const [selectedProductIndex, setSelectedProductIndex] = useState(0);
  const [sampleData, setSampleData] = useState<Record<string, string>>({});

  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { toast } = useToast();

  // Fetch real products on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await feedWatchService.listProducts(feedId, 1, 10);
        if (cancelled) return;
        const products = result.data;
        setPreviewProducts(products);
        if (products.length > 0) {
          setSampleData(flattenRawData(products[0].raw_data));
        }
      } catch {
        // fall back to empty
      }
    })();
    return () => { cancelled = true; };
  }, [feedId]);

  // Clean up timers and preview URL on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Build product labels map for gallery
  const productLabels = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of previewProducts) {
      map.set(p.sort_index, productLabel(p));
    }
    return map;
  }, [previewProducts]);

  // Product thumbnail for status bar
  const currentProductThumb = previewProducts[selectedProductIndex]
    ? resolveProductImageThumb(previewProducts[selectedProductIndex])
    : null;

  const handleProductSelect = useCallback((idx: number) => {
    setSelectedProductIndex(idx);
    const product = previewProducts[idx];
    if (product) {
      setSampleData(flattenRawData(product.raw_data));
    }
    // Clear removeBg for token-based images — bgRemovedSrc is product-specific
    const hasTokenBg = elements.some((el) => el.type === 'image' && el.removeBg && el.src.includes('{{'));
    if (hasTokenBg) {
      history.set(elements.map((el) => {
        if (el.type === 'image' && el.removeBg && el.src.includes('{{')) {
          return { ...el, removeBg: false, bgRemovedSrc: undefined } as CanvasElement;
        }
        return el;
      }));
    }
  }, [previewProducts, elements, history]);

  // ── Immutable element operations ──

  const addElement = useCallback((type: CanvasElementType) => {
    const el = createDefaultElement(type);
    history.set([...elements, el]);
    setSelectedIndex(elements.length);
    toast({ title: `${type.charAt(0).toUpperCase() + type.slice(1)} added` });
  }, [elements, history, toast]);

  const updateElement = useCallback((index: number, updates: Partial<CanvasElement>) => {
    history.set(
      elements.map((el, i) => (i === index ? ({ ...el, ...updates } as CanvasElement) : el))
    );
  }, [elements, history]);

  const deleteElement = useCallback((index: number) => {
    history.set(elements.filter((_, i) => i !== index));
    setSelectedIndex(null);
    toast({ title: 'Element deleted' });
  }, [elements, history, toast]);

  const moveElementLayer = useCallback((index: number, direction: 'up' | 'down') => {
    const targetIdx = direction === 'up' ? index + 1 : index - 1;
    if (targetIdx < 0 || targetIdx >= elements.length) return;
    const next = [...elements];
    const temp = next[index];
    next[index] = next[targetIdx];
    next[targetIdx] = temp;
    history.set(next);
    setSelectedIndex(targetIdx);
  }, [elements, history]);

  const reorderElement = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    const next = [...elements];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    history.set(next);
    setSelectedIndex(toIndex);
  }, [elements, history]);

  const duplicateElement = useCallback((index: number) => {
    const el = elements[index];
    const copy = { ...el, id: `el_${Date.now()}`, x: el.x + 20, y: el.y + 20 };
    history.set([...elements, copy as CanvasElement]);
    setSelectedIndex(elements.length);
    toast({ title: 'Element duplicated' });
  }, [elements, history, toast]);

  // ── Alignment ──

  const handleAlign = useCallback((alignment: string) => {
    if (selectedIndex === null) return;
    const el = elements[selectedIndex];
    const updates: Partial<CanvasElement> = {};

    switch (alignment) {
      case 'left': updates.x = 0; break;
      case 'center-h': updates.x = Math.round((design.width - el.width) / 2); break;
      case 'right': updates.x = design.width - el.width; break;
      case 'top': updates.y = 0; break;
      case 'center-v': updates.y = Math.round((design.height - el.height) / 2); break;
      case 'bottom': updates.y = design.height - el.height; break;
    }

    updateElement(selectedIndex, updates);
  }, [selectedIndex, elements, design.width, design.height, updateElement]);

  // ── Save ──

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const updated = await feedWatchService.updateDesign(feedId, design.id, {
        name: designName,
        elements,
      });
      onDesignUpdated(updated);
      setSaveSuccess(true);
      toast({ title: 'Design saved' });
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err) {
      toast({ title: 'Save failed', description: err instanceof Error ? err.message : 'Unknown error', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render ──

  const handleRenderConfirm = async (productIndices?: number[]) => {
    try {
      setIsRendering(true);
      const renderElements = elements.map((el) => {
        if (el.type === 'image' && el.removeBg && el.bgRemovedSrc && !el.src.includes('{{')) {
          return { ...el, src: el.bgRemovedSrc };
        }
        return el;
      });
      await feedWatchService.updateDesign(feedId, design.id, {
        name: designName,
        elements: renderElements,
      });
      await feedWatchService.triggerRender(feedId, design.id, { productIndices });
      setShowRenderModal(false);
      setShowRenderGallery(true);
      toast({ title: 'Render started' });
    } catch (err) {
      toast({ title: 'Render failed', description: err instanceof Error ? err.message : 'Unknown error', variant: 'destructive' });
    } finally {
      setIsRendering(false);
    }
  };

  // ── Preview ──

  const handlePreview = async () => {
    try {
      setIsPreviewing(true);
      await feedWatchService.updateDesign(feedId, design.id, {
        name: designName,
        elements,
      });
      const currentProduct = previewProducts[selectedProductIndex];
      const blob = await feedWatchService.exportDesign(feedId, design.id, {
        format: 'png',
        quality: 100,
        scale: 2,
        sortIndex: currentProduct?.sort_index,
      });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (err) {
      toast({ title: 'Preview failed', description: err instanceof Error ? err.message : 'Unknown error', variant: 'destructive' });
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleClosePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const handleDownloadFromPreview = () => {
    if (!previewUrl) return;
    const link = document.createElement('a');
    link.href = previewUrl;
    link.download = `${designName || 'design'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Keyboard shortcuts ──

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return;

      // Delete / Backspace
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIndex !== null) {
        e.preventDefault();
        deleteElement(selectedIndex);
        return;
      }

      // Ctrl+D — duplicate
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && selectedIndex !== null) {
        e.preventDefault();
        duplicateElement(selectedIndex);
        return;
      }

      // Ctrl+Z — undo / Ctrl+Shift+Z — redo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) { history.redo(); } else { history.undo(); }
        return;
      }

      // Ctrl+S — save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
        return;
      }

      // Arrow keys — nudge
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && selectedIndex !== null) {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const el = elements[selectedIndex];
        const updates: Partial<CanvasElement> = {};
        switch (e.key) {
          case 'ArrowUp': updates.y = el.y - step; break;
          case 'ArrowDown': updates.y = el.y + step; break;
          case 'ArrowLeft': updates.x = el.x - step; break;
          case 'ArrowRight': updates.x = el.x + step; break;
        }
        updateElement(selectedIndex, updates);
        return;
      }

      // Escape — exit fullscreen or deselect
      if (e.key === 'Escape') {
        if (isFullscreen) {
          setIsFullscreen(false);
        } else {
          setSelectedIndex(null);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, elements, history, deleteElement, duplicateElement, updateElement, isFullscreen]);

  const selectedElement = selectedIndex !== null ? elements[selectedIndex] : null;
  const resolveTokens = !showSource;

  return (
    <TooltipProvider delayDuration={300}>
      <div className={`flex flex-col bg-background overflow-hidden ${
        isFullscreen
          ? 'fixed inset-0 z-50'
          : 'h-[calc(100vh-8rem)] rounded-xl border-2 shadow-lg'
      }`}>
        {/* ── Toolbar ── */}
        <div className="shrink-0 border-b bg-muted/30">
          {/* Row 1: Navigation */}
          <div className="flex items-center gap-2 px-4 py-1.5 border-b border-border/50">
            <CanvasBreadcrumb
              segments={breadcrumbSegments ?? [{ label: 'Designs', onClick: onBack }]}
              editableValue={designName}
              onEditableChange={setDesignName}
            />

            {/* Auto-save indicator */}
            {saveSuccess && (
              <span className="text-xs text-emerald-500 flex items-center gap-1 shrink-0">
                <Check className="h-3 w-3" /> Saved
              </span>
            )}

            {/* Product selector */}
            {previewProducts.length > 0 && (
              <>
                <div className="w-px h-6 bg-border shrink-0" />
                <div className="flex items-center gap-2 shrink-0">
                  {currentProductThumb && (
                    <img
                      src={currentProductThumb}
                      alt=""
                      className="h-7 w-7 rounded object-cover border"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  )}
                  <Select
                    value={String(selectedProductIndex)}
                    onValueChange={(v) => handleProductSelect(Number(v))}
                  >
                    <SelectTrigger className="h-8 w-[200px] min-w-0 text-sm rounded-lg border bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {previewProducts.map((p, i) => (
                        <SelectItem key={p.id} value={String(i)}>
                          #{i + 1} — {productLabel(p)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="flex-1 min-w-0" />

            {/* Save */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={`rounded-lg shrink-0 ${saveSuccess ? 'border-emerald-500 text-emerald-600' : ''}`}
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : saveSuccess ? (
                    <Check className="h-4 w-4 mr-1.5 text-emerald-500" />
                  ) : (
                    <Save className="h-4 w-4 mr-1.5" />
                  )}
                  {saveSuccess ? 'Saved' : 'Save'}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Save (Ctrl+S)</TooltipContent>
            </Tooltip>

            {/* Preview */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-lg shrink-0" onClick={handlePreview} disabled={isPreviewing}>
                  {isPreviewing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />}
                  {isPreviewing ? 'Generating...' : 'Preview'}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Preview with current product data</TooltipContent>
            </Tooltip>

            {/* Renders */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-lg shrink-0" onClick={() => setShowRenderGallery(true)}>
                  <Images className="h-4 w-4 mr-1.5" />
                  Renders
                </Button>
              </TooltipTrigger>
              <TooltipContent>View rendered images</TooltipContent>
            </Tooltip>

            {/* Render */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="sm" className="rounded-lg shrink-0" onClick={() => setShowRenderModal(true)} disabled={isRendering}>
                  {isRendering ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />}
                  Render
                </Button>
              </TooltipTrigger>
              <TooltipContent>Batch render for selected products</TooltipContent>
            </Tooltip>

            {/* Fullscreen */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" onClick={() => setIsFullscreen((f) => !f)}>
                  {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isFullscreen ? 'Exit Fullscreen (Esc)' : 'Fullscreen'}</TooltipContent>
            </Tooltip>
          </div>

          {/* Row 2: Tools */}
          <div className="flex items-center gap-2 px-4 py-1.5">
            {/* Alignment buttons */}
            <div className="flex items-center gap-0.5">
              {[
                { id: 'left', icon: AlignStartHorizontal, tip: 'Align Left' },
                { id: 'center-h', icon: AlignCenterHorizontal, tip: 'Center Horizontal' },
                { id: 'right', icon: AlignEndHorizontal, tip: 'Align Right' },
                { id: 'top', icon: AlignStartVertical, tip: 'Align Top' },
                { id: 'center-v', icon: AlignCenterVertical, tip: 'Center Vertical' },
                { id: 'bottom', icon: AlignEndVertical, tip: 'Align Bottom' },
              ].map((btn) => {
                const Icon = btn.icon;
                return (
                  <Tooltip key={btn.id}>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => handleAlign(btn.id)}
                        disabled={selectedIndex === null}
                        className="h-7 w-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>{btn.tip}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>

            <div className="w-px h-5 bg-border shrink-0" />

            {/* Undo / Redo */}
            <div className="flex items-center gap-0.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={history.undo} disabled={!history.canUndo}>
                    <Undo2 className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Undo (Ctrl+Z)</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={history.redo} disabled={!history.canRedo}>
                    <Redo2 className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Redo (Ctrl+Shift+Z)</TooltipContent>
              </Tooltip>
            </div>

            <div className="w-px h-5 bg-border shrink-0" />

            {/* Zoom */}
            <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-0.5 shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setZoom((z) => Math.max(0.25, z - 0.25))}>
                    <ZoomOut className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Zoom Out</TooltipContent>
              </Tooltip>
              <Select
                value={String(zoom)}
                onValueChange={(v) => setZoom(Number(v))}
              >
                <SelectTrigger className="h-6 w-[60px] border-0 bg-transparent text-xs font-medium px-1 focus:ring-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ZOOM_PRESETS.map((z) => (
                    <SelectItem key={z} value={String(z)}>
                      {Math.round(z * 100)}%
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setZoom((z) => Math.min(2, z + 0.25))}>
                    <ZoomIn className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Zoom In</TooltipContent>
              </Tooltip>
            </div>

            <div className="w-px h-5 bg-border shrink-0" />

            {/* Grid toggle */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={showGrid ? 'default' : 'ghost'}
                  size="sm"
                  className="h-7 w-7 p-0 shrink-0"
                  onClick={() => setShowGrid((g) => !g)}
                >
                  <Grid3x3 className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Toggle Grid</TooltipContent>
            </Tooltip>

            {/* Source / Live toggle */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={showSource ? 'default' : 'outline'}
                  size="sm"
                  className="rounded-lg shrink-0 h-7 text-xs"
                  onClick={() => setShowSource(!showSource)}
                >
                  {showSource ? <Code className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}
                  {showSource ? 'Source' : 'Live'}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{showSource ? 'Showing raw tokens — click for live data' : 'Showing live data — click for raw tokens'}</TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* ── Main area: layers | canvas | property panel ── */}
        <div className="flex-1 flex overflow-hidden">
          {/* Layers panel */}
          <LayersPanel
            elements={elements}
            selectedIndex={selectedIndex}
            onSelect={setSelectedIndex}
            onUpdateElement={updateElement}
            onMoveLayer={moveElementLayer}
            onReorderLayer={reorderElement}
            onAddElement={addElement}
          />

          {/* Canvas area */}
          <CanvasWorkspace
            elements={elements}
            selectedIndex={selectedIndex}
            canvasWidth={design.width}
            canvasHeight={design.height}
            zoom={zoom}
            isPreview={resolveTokens}
            sampleData={sampleData}
            showGrid={showGrid}
            onSelectElement={setSelectedIndex}
            onUpdateElement={updateElement}
            onAddElement={addElement}
          />

          {/* Property panel */}
          <div className="w-[280px] shrink-0 border-l bg-background overflow-y-auto">
            {selectedElement ? (
              <PropertyPanel
                element={selectedElement}
                detectedFields={detectedFields}
                sampleData={sampleData}
                onUpdate={(updates) => updateElement(selectedIndex!, updates)}
                onDelete={() => deleteElement(selectedIndex!)}
                onMoveLayer={(dir) => moveElementLayer(selectedIndex!, dir)}
                onDuplicate={() => duplicateElement(selectedIndex!)}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground px-6 gap-4">
                <div className="text-center">
                  <p className="text-sm font-medium">No element selected</p>
                  <p className="text-xs mt-1">Click an element on the canvas to edit it</p>
                </div>
                <div className="border rounded-lg p-3 w-full bg-muted/20">
                  <p className="text-xs font-medium mb-2">Quick Actions</p>
                  <div className="space-y-1.5">
                    {(['text', 'image', 'rect', 'circle'] as CanvasElementType[]).map((type) => (
                      <button
                        key={type}
                        onClick={() => addElement(type)}
                        className="flex items-center gap-2 w-full px-2 py-1.5 rounded text-xs hover:bg-muted transition-colors text-left"
                      >
                        <span className="w-4 h-4 rounded bg-primary/10 flex items-center justify-center text-primary text-[10px] font-bold">+</span>
                        Add {type.charAt(0).toUpperCase() + type.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="border rounded-lg p-3 w-full bg-muted/20">
                  <p className="text-xs font-medium mb-2">Keyboard Shortcuts</p>
                  <div className="grid grid-cols-2 gap-1 text-[10px]">
                    <span className="text-muted-foreground">Ctrl+Z</span><span>Undo</span>
                    <span className="text-muted-foreground">Ctrl+Shift+Z</span><span>Redo</span>
                    <span className="text-muted-foreground">Ctrl+D</span><span>Duplicate</span>
                    <span className="text-muted-foreground">Ctrl+S</span><span>Save</span>
                    <span className="text-muted-foreground">Delete</span><span>Remove</span>
                    <span className="text-muted-foreground">Arrows</span><span>Nudge (1px)</span>
                    <span className="text-muted-foreground">Shift+Arrows</span><span>Nudge (10px)</span>
                    <span className="text-muted-foreground">Esc</span><span>Deselect</span>
                  </div>
                </div>
                {detectedFields.length > 0 && (
                  <p className="text-[10px] text-center text-muted-foreground/60">
                    {detectedFields.length} product fields available for {'{{field}}'} tokens
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Status bar ── */}
        <div className="shrink-0 border-t bg-muted/20 px-4 py-1.5 flex items-center gap-4 text-xs text-muted-foreground">
          <span>{design.width} x {design.height}</span>
          <span>Zoom: {Math.round(zoom * 100)}%</span>
          <span>{elements.length} element{elements.length !== 1 ? 's' : ''}</span>
          <div className="flex-1" />
          {previewProducts.length > 0 && (
            <span>Product {selectedProductIndex + 1} of {previewProducts.length}</span>
          )}
        </div>

        {/* Render product selection modal */}
        <RenderProductModal
          open={showRenderModal}
          onClose={() => setShowRenderModal(false)}
          feedId={feedId}
          totalProducts={totalProducts ?? 0}
          isRendering={isRendering}
          onConfirm={handleRenderConfirm}
        />

        {/* Preview Modal */}
        {previewUrl && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70" onClick={handleClosePreview}>
            <div className="relative max-w-[90vw] max-h-[90vh] flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
              <img
                src={previewUrl}
                alt="Export preview"
                className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
              />
              <div className="flex items-center gap-3">
                <button
                  onClick={handleDownloadFromPreview}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  <Download className="h-4 w-4" />
                  Download
                </button>
                <button
                  onClick={handleClosePreview}
                  className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Render gallery modal */}
        {showRenderGallery && (
          <RenderGallery
            feedId={feedId}
            designId={design.id}
            productLabels={productLabels}
            onClose={() => setShowRenderGallery(false)}
          />
        )}

        {/* Onboarding */}
        <CanvasOnboarding />
      </div>
    </TooltipProvider>
  );
}
